import React, {useEffect, useState} from 'react'
import {
    Box,
    Link,
    useMultiStyleConfig,
    Heading,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getPDPath} from '../../utils/custom-utils'

const getIntanceTimeZone = () => {
    const pacificStr = new Intl.DateTimeFormat('en-US', {
        timeZone: 'US/Pacific',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(new Date())

    return new Date(pacificStr)
}

const CountdownTimer = () => {
    const {site} = useMultiSite()
    const config = getConfig()
    const siteId = site && site.id ? site.id : config?.app?.defaultSite
    const [timerData, setTimerData] = useState(null)
    const [timeLeft, setTimeLeft] = useState({hours: '00', minutes: '00', seconds: '00'})
    const [expired, setExpired] = useState(false)

    const styles = useMultiStyleConfig('countDownTimer')
    const layoutStyles = useMultiStyleConfig('containerStyles')

    useEffect(() => {
        const el = document.querySelector('.countdown-globle-component')
        if (!el) return

        const countdownEndTime = el.getAttribute('data-countdown')
        const endTime = countdownEndTime ? new Date(countdownEndTime).getTime() : null

        const data = {
            containerColor: el.getAttribute('data-countdown-container-color'),
            himeImage: el.getAttribute('data-hime-image'),
            title: el.getAttribute('data-title'),
            subTitle: el.getAttribute('data-sub-title'),
            buttonText1: el.getAttribute('data-button-text-1'),
            buttonLink1: el.getAttribute('data-button-1-link'),
            buttonTextColor1: el.getAttribute('data-button-text-1-color'),
            buttonText2: el.getAttribute('data-button-text-2'),
            buttonTextColor2: el.getAttribute('data-button-text-2-color'),
            buttonLink2: el.getAttribute('data-button-2-link'),
            endTime
        }

        setTimerData(data)
    }, [])

    useEffect(() => {
        if (!timerData?.endTime) return

        const interval = setInterval(() => {
            const now = getIntanceTimeZone().getTime()
            const distance = timerData.endTime - now

            if (distance <= 0) {
                setExpired(true)
                clearInterval(interval)
                return
            }

            const hours = String(Math.floor((distance / (1000 * 60 * 60)) % 24)).padStart(2, '0')
            const minutes = String(Math.floor((distance / (1000 * 60)) % 60)).padStart(2, '0')
            const seconds = String(Math.floor((distance / 1000) % 60)).padStart(2, '0')

            setTimeLeft({hours, minutes, seconds})
        }, 1000)

        return () => clearInterval(interval)
    }, [timerData])

    // Generate image URL
    const getImageUrl = () => {
        if (!timerData?.himeImage) return ''
        if (timerData.himeImage.startsWith('http')) return timerData.himeImage

        const fileName = timerData.himeImage.split('?')[0]
        return `${window.location.origin}/on/demandware.static/-/Sites-${siteId}-Library/default/images/${fileName}`
    }
    const imageUrl = getImageUrl()

    if (!timerData) return null

    return (
        <Box
            data-test-id="countdowntimer-block"
            {...styles.countdowntimerBlock}
            style={{backgroundColor: timerData.containerColor}}
        >
            <Link
                className={`${timerData.buttonText2 ? '' : 'no-event-link'}`}
                href={getPDPath(timerData.buttonLink2)}
                aria-label="Go to Link"
                {...styles.mobileCountdownLink}
            />
            <Box
                className="countdowntimer-inner-block"
                {...styles.countdowntimerInnerBlock}
                py={{base: '0px', sm: '11px', md: '12px'}}
            >
                {imageUrl && (
                    <Box
                        className="image-block"
                        backgroundImage={`url(${imageUrl})`}
                        {...styles.imageBlock}
                    />
                )}

                <Box
                    className="countdowntimer-text-timer-block"
                    sx={{
                        ...styles.countdowntimerTextTimerBlock,
                        ...layoutStyles.containerStyles
                    }}
                >
                    <Box {...styles.countdowntimerTextTimerLeftBlock}>
                        {timerData.title && (
                            <Heading as="h2" className="countdown-title" {...styles.mainHeading}>
                                {timerData.title}
                            </Heading>
                        )}
                        {timerData.subTitle && (
                            <Text as="p" className="countdown-sub-title" {...styles.subHeading}>
                                {timerData.subTitle}
                            </Text>
                        )}
                    </Box>

                    <Box {...styles.countdowntimerTextTimerRightBlock}>
                        {timerData.endTime && !expired && (
                            <Box {...styles.clockBlock}>
                                <Box {...styles.digitsBg}>{timeLeft.hours}</Box>
                                <Box {...styles.colon}>:</Box>
                                <Box {...styles.digitsBg}>{timeLeft.minutes}</Box>
                                <Box {...styles.colon}>:</Box>
                                <Box {...styles.digitsBg}>{timeLeft.seconds}</Box>
                            </Box>
                        )}

                        {timerData.buttonText1 && (
                            <Box display={['none', 'none', 'block']} {...styles.buttonBlock}>
                                <Link
                                    href={getPDPath(timerData.buttonLink1)}
                                    aria-label={timerData.buttonText2}
                                    {...styles.countdownLink}
                                    style={{color: timerData.buttonTextColor1}}
                                >
                                    {timerData.buttonText1}
                                </Link>
                            </Box>
                        )}

                        {timerData.buttonText2 && (
                            <Box display={['none', 'block']} {...styles.buttonBlock}>
                                <Link
                                    href={getPDPath(timerData.buttonLink2)}
                                    aria-label={timerData.buttonText2}
                                    {...styles.countdownLink}
                                    style={{color: timerData.buttonTextColor2, background: 'black'}}
                                >
                                    {timerData.buttonText2}
                                </Link>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default CountdownTimer
